import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus, ReceiptText } from "lucide-react";
import { DashboardShell, PlaceholderCard } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import { fetchInvoices, formatMoney, INVOICE_STATUSES, INVOICE_TYPES } from "@/lib/invoices";

export const Route = createFileRoute("/_authenticated/partner/invoices/")({
  head: () => ({
    meta: [
      { title: "Invoices · Landoo Partner" },
      {
        name: "description",
        content: "Create and track customer invoices for property and parking services.",
      },
    ],
  }),
  component: InvoicesPage,
});

function InvoicesPage() {
  const { user } = useAuth();
  const { data = [], isLoading } = useQuery({
    queryKey: ["invoices", user?.id],
    queryFn: () => fetchInvoices(user!.id),
    enabled: !!user,
  });

  return (
    <DashboardShell title="Invoices" role="Partner" items={PARTNER_NAV}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <ReceiptText className="h-4 w-4" />
          {isLoading ? "Loading…" : `${data.length} invoice(s)`}
        </div>
        <Link
          to="/partner/invoices/new"
          className="inline-flex items-center gap-1.5 rounded-full bg-coral px-3.5 py-2 text-xs font-semibold text-white shadow-soft"
        >
          <Plus className="h-4 w-4" /> New invoice
        </Link>
      </div>

      {data.length > 0 ? (
        <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Invoice</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Total</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((inv) => {
                const st = INVOICE_STATUSES.find((s) => s.value === inv.payment_status);
                return (
                  <tr key={inv.id} className="border-t border-border">
                    <td className="px-3 py-2 font-semibold text-brown-dark">
                      <Link
                        to="/partner/invoices/$invoiceId"
                        params={{ invoiceId: inv.id }}
                        className="hover:underline"
                      >
                        {inv.invoice_number}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{inv.customer_name}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {INVOICE_TYPES.find((t) => t.value === inv.invoice_type)?.label}
                    </td>
                    <td className="px-3 py-2 text-xs">{inv.invoice_date}</td>
                    <td className="px-3 py-2 font-semibold text-coral">{formatMoney(inv.total)}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded-full px-2 py-1 text-[10px] font-semibold ${st?.cls}`}
                      >
                        {st?.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-4">
          <PlaceholderCard
            title="No invoices yet"
            description="Create property, rent, lease, parking or custom service invoices for your customers. PDF download, share and payment collection are placeholders for now."
          />
        </div>
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <PlaceholderCard
          title="Download PDF"
          description="PDF generation will be available in a future release."
        />
        <PlaceholderCard
          title="Payments"
          description="Online payment collection is not enabled yet."
        />
      </div>
    </DashboardShell>
  );
}
